import { ExportDto } from '@ecosystem/export-api';
import { ExportFormat } from '@ecosystem/newron-client-lib';
import { BlobExportFile } from './SaveFile';
import { ImageURLData, InvokeExport, UseMultipleChartsType } from '@ecosystem/export-utils';
import { FactRevenue, Option, PageName } from '@utils/pageConfig/Types';
import { ExportPerformanceBySegmentResp, KpiDataDto } from '@data-visualization/performance-analysis-line-chart/Types';
import { StackedChartResp } from '@data-visualization/stacked-chart/Types';
import { PerformanceAnalysisboardPg } from '@models/PerformanceAnalysisboard';
import { CategoryLeaderboardPg } from '@models/CategoryLeaderboard';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import { OpportunityReviewTableContent } from '@models/OpportunityReview';
import { ExportTrackingPageView } from './export-tracking/ExportTracking';
import { LeaderboardKpi, ProductsCompareExport, ProductsCompareKpi } from '@models/Products';
import { ShareComparisonChartResp } from '@data-visualization/share-comparison-chart/Types';
import { AveragePriceChartResp } from '@data-visualization/average-selling-price-chart/Types';
import { LoadedProductGroupsState } from '@state/feature/availability-pg/AvailabilityPgSingleCountrySlice';
import { LoadedCountriesState } from '@state/feature/availability-country/AvailabilityCountrySlice';
import { ExploreTabView, TradingAssistantTabView } from '@components/tab-nav/TabConfig';
import { CapturedImage } from '@ecosystem/newron-design-system/build/export-utils/captureImage';
import { ExportExploreTrendsStack } from '@data-visualization/trends-stack-chart/Types';
import { SortFieldDisplayProps } from '@components/design/migrated/Table';

// Module name
export enum EnabledExportPages {
  PerformanceAnalysis = 'PerformanceAnalysis',
  CategoryLeaderboard = 'CategoryLeaderboard',
  OpportunityReview = 'OpportunityReview',
  Products = 'Products',
  RetailerOverview = 'RetailerOverview',
  Explore = 'Explore',
  TradingAssistant = 'TradingAssistant',
}

// View type for different type of charts
export enum ViewType {
  Table = 'Table',
  LineChart = 'Line Chart',
  BarChart = 'Bar Chart',
  StackedChart = 'Stacked Chart',
  BubbleChart = 'Bubble Chart',
  CombinedChart = 'Combined Chart',
  ButterflyChart = 'Butterfly Chart',
}

export enum EmptyTabsPage {
  None = 'None',
}

export enum ProductsTabView {
  Leaderboard = 'Leaderboard',
  Trends = 'Trends',
}

/**
 * Types
 */
type ViewsExportModalSettings = { [key in ViewType]?: ExportModalSettings };

export type ExportModalSettings = {
  exportFormats: ExportFormat[];
  modalHeaderText: string;
  modalMessage: string;
  exportModalSettingsOverrides?: {
    [key: string]: ExportModalSettings;
  };
};

export type EnabledExportsMap = {
  [EnabledExportPages.RetailerOverview]: {
    [key in EmptyTabsPage]?: ViewsExportModalSettings;
  };
  [EnabledExportPages.PerformanceAnalysis]: {
    [key in EmptyTabsPage]?: ViewsExportModalSettings;
  };
  [EnabledExportPages.CategoryLeaderboard]: {
    [key in EmptyTabsPage]?: ViewsExportModalSettings;
  };
  [EnabledExportPages.OpportunityReview]: {
    [key in EmptyTabsPage]?: ViewsExportModalSettings;
  };
  [EnabledExportPages.Products]: {
    [key in ProductsTabView.Leaderboard]?: ViewsExportModalSettings;
  } & {
    [key in ProductsTabView.Trends]?: ViewsExportModalSettings;
  };
  [EnabledExportPages.Explore]: {
    [key in ExploreTabView.Leaderboard]?: ViewsExportModalSettings;
  } & {
    [key in ExploreTabView.Trends]?: ViewsExportModalSettings;
  } & {
    [key in ExploreTabView.Segments]?: ViewsExportModalSettings;
  };
  [EnabledExportPages.TradingAssistant]: {
    [key in TradingAssistantTabView.SupplierReview]?: ViewsExportModalSettings;
  };
};

export type ExportTabView = ProductsTabView | EmptyTabsPage | ExploreTabView | TradingAssistantTabView;

export type UseExportModalReturnType = {
  openExportModal: () => void;
  onCloseExportModal: () => void;
  onExportError: (error: Error) => void;
  exportModalFormats: ExportFormat[];
  exportModalHeaderText: string;
  exportModalMessage: string;
  isExportModalOpen: boolean;
  isExportEnabled: boolean;
};
export type ExportFunction = (data: ExportDto) => Promise<BlobExportFile>;

export const PERFORMANCE_BY_SEGMENTS = 'Performance By Segments';

/**
 * Interface
 */
export interface ExportChartOptions {
  exportFormats: ExportFormat[];
  invokeExport?: InvokeExport;
}

export interface LoadedProductGroups {
  loadedProductGroups?: LoadedProductGroupsState;
}

export interface LoadedCountries {
  loadedCountries?: LoadedCountriesState;
}

export interface CreateImagePowerPointParams extends LoadedProductGroups, LoadedCountries {
  filename: string;
  exportFn: ExportFunction;
  appliedCountry: Option[] | undefined;
  appliedPg: Option[] | undefined;
  timePeriod: string;
  comparisonTimePeriod: string;
  periodType: string;
  showPerformanceWithin: string;
  appliedFiltersText: string;
  invokeExport?: InvokeExport;
  selectedKpi: Option;
  segmentBy: string;
  title: string;
  exportImage?: CapturedImage | CapturedImage[];
  subTitle: string;
  pageName?: PageName;
  pageView?: ExportTrackingPageView;
  pagesConfig?: PagesConfig;
}

export interface CreateMultiImagePowerPointParams extends LoadedProductGroups, LoadedCountries {
  filename: string;
  exportFn: ExportFunction;
  exportImage?: CapturedImage | CapturedImage[];
  appliedCountry: Option[] | undefined;
  appliedPg: Option[] | undefined;
  timePeriod: string;
  comparisonTimePeriod: string;
  periodType: string;
  showPerformanceWithin: string;
  appliedFiltersText: string;
  selectedKpi: Option;
  sortedBy?: SortFieldDisplayProps;
  segmentBy: string;
  chartTitles: string[];
  multipleChartsExport?: UseMultipleChartsType;
  chartSubTitles: string[];
  pageName?: PageName;
  pageView?: ExportTrackingPageView;
  pagesConfig?: PagesConfig;
}

export interface CreatePowerPointTableParams {
  filename: string;
  exportFn: ExportFunction;
  exportImage?: CapturedImage | CapturedImage[];
  appliedCountry: Option[] | undefined;
  appliedPg: Option[] | undefined;
  timePeriod: string;
  comparisonTimePeriod: string;
  periodType: string;
  appliedFiltersText: string;
  selectedKpi: Option;
  chartTitles: string[];
  multipleChartsExport?: UseMultipleChartsType;
  chartSubTitles: string[];
  pageName?: PageName;
  pageView?: ExportTrackingPageView;
  featureData: ProductsCompareKpi;
  imageData: CapturedImage | CapturedImage[] | (ImageURLData | undefined)[] | undefined;
  currency: string;
}

export interface GetExportImagePowerPointParams extends LoadedProductGroups, LoadedCountries {
  filename: string;
  appliedCountry: Option[] | undefined;
  appliedPg: Option[] | undefined;
  timePeriod: string;
  comparisonTimePeriod: string;
  periodType: string;
  showPerformanceWithin: string;
  appliedFiltersText: string;
  imageData: ImageURLData;
  selectedKpi: Option;
  segmentBy: string;
  title: string;
  subTitle: string;
  pageName?: PageName;
  pageView?: ExportTrackingPageView;
  pagesConfig?: PagesConfig;
}

export interface GetExportMultiImagePowerPointParams extends LoadedProductGroups, LoadedCountries {
  filename: string;
  appliedCountry: Option[] | undefined;
  appliedPg: Option[] | undefined;
  timePeriod: string;
  comparisonTimePeriod: string;
  periodType: string;
  showPerformanceWithin: string;
  appliedFiltersText: string;
  imageData?: (ImageURLData | undefined)[] | CapturedImage | CapturedImage[];
  exportImage?: CapturedImage | CapturedImage[];
  selectedKpi: Option;
  sortedBy?: SortFieldDisplayProps;
  segmentBy: string;
  chartTitles: string[];
  chartSubTitles: string[];
  pageName?: PageName;
  pageView?: ExportTrackingPageView;
  pagesConfig?: PagesConfig;
}

export interface ExcelRowArgs {
  timePeriod: string;
  comparisonTimePeriod: string;
  periodType: string;
  showPerformanceWithin: string;
  appliedFiltersText: string;
  selectedKpi: Option;
}

export interface ExportChartArgs extends LoadedProductGroups, LoadedCountries {
  exportFn: ExportFunction;
  exportFormats: ExportFormat[];
  invokeExport?: InvokeExport;
  title: string;
  exportImage?: CapturedImage | CapturedImage[];
  timePeriod: string;
  comparisonTimePeriod: string;
  appliedCountry: Option[] | undefined;
  appliedPg: Option[] | undefined;
  stackTrendChartExcelData?: ExportExploreTrendsStack;
  data:
    | ExportPerformanceBySegmentResp
    | StackedChartResp[]
    | PerformanceAnalysisboardPg[]
    | CategoryLeaderboardPg[]
    | OpportunityReviewTableContent[]
    | KpiDataDto[]
    | ProductsCompareExport[]
    | ProductsCompareKpi
    | LeaderboardKpi[]
    | ShareComparisonChartResp
    | AveragePriceChartResp;
  summaryData?: CategoryLeaderboardPg[] | LeaderboardKpi[];
  factRevenue: FactRevenue;
  periodType: string;
  showPerformanceWithin: string;
  appliedFiltersText: string;
  selectedKpi: Option;
  sortedBy?: SortFieldDisplayProps;
  segmentBy: string;
  pageName: PageName;
  pagesConfig?: PagesConfig;
  pageView?: ExportTrackingPageView;
  multipleChartsExport?: UseMultipleChartsType;
  chartTitles?: string[];
  chartSubTitles?: string[];
  volume?: string;
  subtitle?: string;
  retailClientName?: string;
  isDeepDiveView?: boolean;
  sortedDeepDiveBrands?: string[];
}

export interface ExcelExportParams extends LoadedProductGroups, LoadedCountries {
  filename: string;
  exportFn: ExportFunction;
  appliedCountry: Option[] | undefined;
  appliedPg: Option[] | undefined;
  data: any;
  factRevenue: FactRevenue;
  excelRowArgs: ExcelRowArgs;
  title: string;
  stackTrendChartExcelData?: ExportExploreTrendsStack;
  pageName?: PageName;
  volume?: string;
  pagesConfig?: PagesConfig;
}

export interface ExportTableParams extends LoadedProductGroups, LoadedCountries {
  filename: string;
  exportFn: ExportFunction;
  appliedCountry: Option[] | undefined;
  appliedPg: Option[] | undefined;
  data: any;
  summaryData?: CategoryLeaderboardPg[] | LeaderboardKpi[];
  factRevenue: FactRevenue;
  excelRowArgs: ExcelRowArgs;
  pageName: PageName;
  worksheetName: string;
  pagesConfig?: PagesConfig;
  pageView: ExportTrackingPageView | undefined;
  volume?: string;
  retailClientName?: string;
  isDeepDiveView?: boolean;
  segmentBy?: string;
  sortedDeepDiveBrands?: string[];
}

export const EXPORT_CHART_SIZE = { width: 1044, height: 450 };
export const EXPORT_RENDERING_RESOLUTION = { width: 1920, height: 1080 };
